var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import { clsx } from 'clsx';
import MediaQuery from 'react-responsive';
import { isFocusable, tabbable } from 'tabbable';
import Accordion from '@telia-front/accordion';
import Button from '@telia-front/button';
import Heading from '@telia-front/heading';
import Modal, { ModalContent, ModalFooter, ModalHeader, ModalProvider } from '@telia-front/modal';
import Skeleton, { SkeletonItem } from '@telia-front/skeleton';
import { ThemeContext } from '@telia-front/theme-provider';
import FilterHeader from '../filter-header/filter-header';
import { FilterContext } from './filter-context';
export class Filter extends React.Component {
    constructor(props) {
        super(props);
        this.filter = null;
        this.handleResize = () => {
            const isLargeScreen = window.matchMedia(`(min-width: ${this.context.media.width.md.min})`).matches;
            if (isLargeScreen && this.props.isOpen) {
                this.closeModal();
            }
            else if (!isLargeScreen && this.props.isOpen) {
                this.openModal();
            }
        };
        this.restoreFocus = () => {
            requestAnimationFrame(() => {
                if (this.state.elementToRestoreFocus && isFocusable(this.state.elementToRestoreFocus)) {
                    this.state.elementToRestoreFocus.focus();
                }
            });
        };
        this.handleCloseClick = (event) => {
            event.preventDefault();
            if (this.props.onToggle) {
                this.props.onToggle(false);
            }
        };
        this.isFilterSectionHeader = (element) => {
            return !!element && !!element.className && element.className.includes('accordion__title');
        };
        this.onKeyDown = (event) => {
            const componentRef = this.filter;
            if (componentRef) {
                const tabbableElements = tabbable(componentRef);
                const filterSectionHeaders = tabbableElements.filter(e => this.isFilterSectionHeader(e));
                if (filterSectionHeaders.length) {
                    const firstSectionHeader = filterSectionHeaders[0];
                    const lastSectionHeader = filterSectionHeaders[filterSectionHeaders.length - 1];
                    const activeSectionHeader = filterSectionHeaders.find(e => e === document.activeElement);
                    switch (event.key) {
                        case 'ArrowDown':
                        case 'Down':
                            if (document.activeElement === lastSectionHeader) {
                                event.preventDefault();
                                firstSectionHeader.focus();
                            }
                            else if (activeSectionHeader) {
                                event.preventDefault();
                                const focusedElementIndex = filterSectionHeaders.indexOf(activeSectionHeader);
                                filterSectionHeaders[focusedElementIndex + 1].focus();
                            }
                            break;
                        case 'Up':
                        case 'ArrowUp':
                            if (document.activeElement === firstSectionHeader) {
                                event.preventDefault();
                                lastSectionHeader.focus();
                            }
                            else if (activeSectionHeader) {
                                event.preventDefault();
                                const focusedElementIndex = filterSectionHeaders.indexOf(activeSectionHeader);
                                filterSectionHeaders[focusedElementIndex - 1].focus();
                            }
                            break;
                        case 'End':
                            if (activeSectionHeader) {
                                event.preventDefault();
                                lastSectionHeader.focus();
                            }
                            break;
                        case 'Home':
                            if (activeSectionHeader) {
                                event.preventDefault();
                                firstSectionHeader.focus();
                            }
                            break;
                    }
                }
            }
        };
        this.state = {
            isModalOpen: false,
            elementToRestoreFocus: null,
            isMounted: false,
        };
    }
    componentDidMount() {
        const isLargeScreen = window.matchMedia(`(min-width: ${this.context.media.width.md.min})`).matches;
        this.setState({ isMounted: true });
        if (this.props.isOpen && !isLargeScreen) {
            this.openModal();
        }
        window.addEventListener('resize', this.handleResize);
    }
    componentDidUpdate(prevProps) {
        const isLargeScreen = window.matchMedia(`(min-width: ${this.context.media.width.md.min})`).matches;
        if (this.props.isOpen && !prevProps.isOpen && !isLargeScreen) {
            this.openModal();
        }
        if (!this.props.isOpen && prevProps.isOpen) {
            if (!isLargeScreen) {
                this.closeModal(this.restoreFocus);
            }
            else {
                this.closeModal();
            }
        }
    }
    componentWillUnmount() {
        window.removeEventListener('resize', this.handleResize);
    }
    initFocus() {
        this.setState({ elementToRestoreFocus: document.activeElement });
        requestAnimationFrame(() => {
            if (this.filter) {
                const firstTabbableElement = tabbable(this.filter).find(this.isFilterSectionHeader);
                if (firstTabbableElement) {
                    firstTabbableElement.focus();
                    return;
                }
                this.filter.focus();
            }
        });
    }
    openModal() {
        if (!this.state.isModalOpen) {
            this.setState({
                isModalOpen: true,
            }, this.initFocus);
        }
    }
    closeModal(callback = () => undefined) {
        if (this.state.isModalOpen) {
            this.setState({
                isModalOpen: false,
            }, callback);
        }
    }
    renderSaveButton(button) {
        return (React.createElement(Button, Object.assign({}, button, { type: this.props.purpose === 'employee' ? 'employee' : undefined, block: true, className: clsx('filter__save', button.className) })));
    }
    renderClearButton(button) {
        return React.createElement(Button, Object.assign({ link: true }, button, { type: this.props.purpose === 'employee' ? 'employee' : undefined }));
    }
    renderSkeleton(props) {
        const { skeleton } = props;
        return (React.createElement(Skeleton, { className: "filter", label: skeleton.label },
            React.createElement(SkeletonItem, { className: "h2 filter__heading", width: "60%" }),
            React.createElement(SkeletonItem, { height: "calc(100vh - 87px)" })));
    }
    renderHeading() {
        var _a, _b;
        const headingLevel = ((_a = this.props.heading) === null || _a === void 0 ? void 0 : _a.level) || this.props.headingLevel || 1;
        const headingClassName = ((_b = this.props.heading) === null || _b === void 0 ? void 0 : _b.className) || 'h4';
        if (this.props.heading) {
            const _c = this.props.heading, { className, level } = _c, rest = __rest(_c, ["className", "level"]);
            return (React.createElement(Heading, Object.assign({ level: headingLevel, className: clsx('filter__heading', headingClassName) }, rest), this.props.title));
        }
        return (React.createElement(Heading, { level: headingLevel, className: clsx('filter__heading', headingClassName) }, this.props.title));
    }
    renderFilter() {
        if ('skeleton' in this.props) {
            return this.renderSkeleton(this.props);
        }
        const childrenArray = React.Children.toArray(this.props.children);
        const filteredHeader = childrenArray.find(child => React.isValidElement(child) && child.type === FilterHeader);
        const filteredChildren = childrenArray.filter(child => !React.isValidElement(child) || child.type !== FilterHeader);
        return (React.createElement(FilterContext.Provider, { value: { purpose: this.props.purpose || 'default' } },
            React.createElement("div", { className: "filter", ref: element => (this.filter = element), onKeyDown: this.onKeyDown },
                React.createElement("div", { className: "filter__main" },
                    this.renderHeading(),
                    filteredHeader,
                    React.createElement(Accordion, { multiple: true }, filteredChildren)),
                !this.state.isModalOpen && (React.createElement("div", { className: "filter__footer" },
                    this.props.saveButton && this.renderSaveButton(this.props.saveButton),
                    this.props.clearButton && this.renderClearButton(this.props.clearButton))))));
    }
    renderFilterInModal() {
        return (React.createElement(ModalProvider, null,
            React.createElement(Modal, { isOpen: this.props.isOpen, isFullScreen: true, isClosable: "backdrop", onClose: this.handleCloseClick, hasContentComponent: true },
                React.createElement(ModalHeader, { backButton: {
                        icon: '#arrow-left',
                        text: 'Tagasi',
                        className: 'filter__close',
                        onClick: this.handleCloseClick,
                    } },
                    React.createElement("div", { className: "text-right" }, this.props.clearButton && (React.createElement(Button, Object.assign({ link: true, onClick: this.handleCloseClick }, this.props.clearButton, { type: this.props.purpose === 'employee' ? 'employee' : undefined }))))),
                React.createElement(ModalContent, null, this.renderFilter()),
                this.props.saveButton && React.createElement(ModalFooter, { isSticky: true }, this.renderSaveButton(this.props.saveButton)))));
    }
    render() {
        return React.createElement(MediaQuery, { minWidth: this.context.media.width.md.min }, matches => (matches ? this.renderFilter() : this.renderFilterInModal()));
    }
}
Filter.contextType = ThemeContext;
export { Filter as default };
